import { Box, IconButton, Modal, SvgIcon } from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import {
	borderRadius,
	boxShadows,
	customSizes,
	palette,
	spaces,
} from '../../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../../utils/getMuiIcon';
import { downloadImage } from '../utils';

type Props = {
	open: boolean;
	src?: string;
	alt?: string;
	onClose: () => void;
	onDownload?: () => void;
};

const floatingButtonStyles = {
	position: 'absolute' as const,
	zIndex: 1,
	width: customSizes.imagePreviewOverlay.buttonSize,
	height: customSizes.imagePreviewOverlay.buttonSize,
	backgroundColor: palette.common.white,
	boxShadow: boxShadows.elevation3,
	color: palette.text.primary,
	'&:hover': {
		backgroundColor: palette.common.white,
		color: palette.primary.main,
	},
	'&.Mui-disabled': {
		backgroundColor: palette.common.white,
		opacity: 0.6,
	},
};

const buttonTopOffset = `calc(env(safe-area-inset-top, 0px) + ${spaces.md})`;

const ImagePreviewOverlay: React.FC<Props> = ({ open, src, alt, onClose, onDownload }) => {
	const { t } = useTranslation();

	const handleDownload = () => {
		if (!src) return;
		onDownload?.();
		downloadImage(src, alt);
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			data-testid="image-preview-overlay"
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				// Sit above the AI assistant dialog (1400), alerts (1500),
				// and any popper-based overlays (1401) so the preview is always on top.
				zIndex: (theme) => theme.zIndex.modal + 300,
			}}
		>
			<Box
				onClick={(e) => {
					if (e.target === e.currentTarget) onClose();
				}}
				data-testid="image-preview-overlay-backdrop"
				sx={{
					position: 'relative',
					width: '100vw',
					height: '100dvh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					outline: 'none',
				}}
			>
				<IconButton
					aria-label={t('Close')}
					onClick={onClose}
					data-testid="image-preview-overlay-close"
					sx={{
						...floatingButtonStyles,
						top: buttonTopOffset,
						left: spaces.md,
					}}
				>
					<SvgIcon
						sx={{ width: 24, height: 24 }}
						viewBox="0 0 24 24"
						component={getMuiIcon('back')}
						aria-hidden="true"
					/>
				</IconButton>
				<IconButton
					aria-label={t('Download')}
					onClick={handleDownload}
					disabled={!src}
					data-testid="image-preview-overlay-download"
					sx={{
						...floatingButtonStyles,
						top: buttonTopOffset,
						right: spaces.md,
					}}
				>
					<SvgIcon
						sx={{ width: 24, height: 24 }}
						viewBox="0 0 24 24"
						component={getMuiIcon('download')}
						aria-hidden="true"
					/>
				</IconButton>
				{src && (
					<Box
						component="img"
						src={src}
						alt={alt}
						data-testid="image-preview-overlay-image"
						sx={{
							maxWidth: customSizes.imagePreviewOverlay.imageMaxWidth,
							maxHeight: customSizes.imagePreviewOverlay.imageMaxHeight,
							objectFit: 'contain',
							borderRadius: borderRadius.md,
						}}
					/>
				)}
			</Box>
		</Modal>
	);
};

export default ImagePreviewOverlay;
